import Config from "./config.js";
import Loader from "./loader.js";

/**
 * Api — the Chili Piper methods. index.js composes these onto the object it publishes as
 * ThryvApi.chiliPiper, so `this` is that object at call time.
 *
 * Usage and options are documented in index.js.
 */
const Api = {
  Config,

  /** Whether the vendor script is up. Mostly for debugging. */
  ready: () => Loader.ready(),

  /**
   * Open the scheduler for a lead.
   *
   * @param  {object} options  see index.js
   * @return {Promise<boolean>}  false when routing failed (CP called onError)
   */
  book(options = {}) {
    const account = options.account || Config.account();
    const router = options.router;
    const lead = options.lead || {};

    // REQUIRED. Guessing one routes the lead to whichever queue happened to be the default, which
    // the page cannot tell apart from success.
    if (!router) {
      console.warn("[ThryvApi.chiliPiper] book() needs a router — nothing was booked");

      if (typeof options.onError === "function") options.onError();

      return Promise.resolve(false);
    }

    return Loader.load(account)
      .then(
        (ChiliPiper) =>
          new Promise((resolve) => {
            const payload = {
              lead,
              onSuccess: () => {
                if (typeof options.onSuccess === "function") options.onSuccess();

                resolve(true);
              },
              onError: () => {
                if (typeof options.onError === "function") options.onError();
                else console.warn("[ThryvApi.chiliPiper] Chili Piper could not route this lead");

                resolve(false);
              },
            };

            // Two mutually exclusive shapes, and CP rejects the wrong one:
            //   trigger  → flows configured with an in-app button; lead is keyed PersonEmail
            //   map:true → the generic ThirdPartyForm shape
            if (options.trigger) payload.trigger = options.trigger;
            else payload.map = true;

            ChiliPiper.submit(account, router, payload);
          }),
      )
      .catch((error) => {
        console.warn("[ThryvApi.chiliPiper] could not load Chili Piper:", error);

        if (typeof options.onError === "function") options.onError(error);

        return false;
      });
  },

  /**
   * Bind CP to a HubSpot form so it takes over on submit — the vendor's `deploy` path.
   *
   * @param  {object} options  { formIds, router, formType?, account?, meetingTypeId?, onBeforeSubmit?, onSuccess? }
   *
   * onSuccess receives CP's payload (the booking, its redirectUrl). Leave it out to keep CP's own post-booking
   * redirect — configured per router in CP's admin; passing one takes that over for this form.
   * @return {Promise<boolean>}
   */
  deploy(options = {}) {
    const account = options.account || Config.account();
    const router = options.router;

    // REQUIRED, same as book().
    if (!router) {
      console.warn("[ThryvApi.chiliPiper] deploy() needs a router — the form was not bound");

      return Promise.resolve(false);
    }

    return Loader.load(account)
      .then((ChiliPiper) => {
        ChiliPiper.deploy(account, router, {
          formType: options.formType || "Hubspot",
          formIds: options.formIds || [],
          onBeforeSubmit: options.onBeforeSubmit,
          ...(options.meetingTypeId ? { meetingTypeId: options.meetingTypeId } : {}),
          ...(typeof options.onSuccess === "function" ? { onSuccess: options.onSuccess } : {}),
        });

        return true;
      })
      .catch((error) => {
        console.warn("[ThryvApi.chiliPiper] could not load Chili Piper:", error);

        return false;
      });
  },
};

export default Api;
